import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import ComunicacionesApi from "api/comunicacionesApi";
import useComunicacionEditorImages from "../hooks/useComunicacionEditorImages";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
`;

const EditComunicacion = ({ open, onClose, onSuccess, comunicacion }) => {
  const [formData, setFormData] = useState({
    asunto: "",
    contenido: "",
    destinatarios: [],
  });
  const [errors, setErrors] = useState({});
  const quillRef = useRef(null);
  const [contenido, setContenido] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Hook para manejo de imágenes (ahora CON comunicacionId existente)
  const {
    uploadingImage,
    imageError,
    setupImageHandler,
    cleanOrphanImages,
    getQuillModules,
    getQuillFormats,
    clearError,
  } = useComunicacionEditorImages(
    comunicacion?.proceso_identificador || null,
    comunicacion?.id || null
  );

  // Cargar datos de la comunicación cuando se abre el modal
  useEffect(() => {
    if (open && comunicacion) {
      setLoadingData(true);
      loadComunicacionData();
    }
  }, [open, comunicacion]);

  // Configurar el handler de imágenes cuando el editor esté listo
  useEffect(() => {
    if (quillRef.current && comunicacion?.proceso_identificador && comunicacion?.id) {
      setupImageHandler(quillRef, "contenido");
    }
  }, [setupImageHandler, comunicacion]);

  const loadComunicacionData = async () => {
    try {
      if (!comunicacion) {
        setErrors({ errorGeneral: "No se proporcionó una comunicación válida para editar." });
        return;
      }

      // Si ya tenemos todos los datos en el prop comunicacion, usarlos directamente
      if (
        comunicacion.destinatarios &&
        comunicacion.asunto &&
        comunicacion.contenido !== undefined
      ) {
        setFormData({
          asunto: comunicacion.asunto || "",
          contenido: comunicacion.contenido || "",
          destinatarios: comunicacion.destinatarios || [],
        });
        setContenido(comunicacion.contenido || "");
      } else {
        // Si no, hacer una llamada para obtener los datos completos
        console.log("Obteniendo datos completos de la comunicación...");
        const response = await ComunicacionesApi.obtenerComunicacionById(comunicacion.id);
        const comunicacionCompleta = response.data;

        setFormData({
          asunto: comunicacionCompleta.asunto || "",
          contenido: comunicacionCompleta.contenido || "",
          destinatarios: comunicacionCompleta.destinatarios || [],
        });
        setContenido(comunicacionCompleta.contenido || "");
      }
    } catch (error) {
      console.error("Error cargando datos de comunicación:", error);
      setErrors({
        errorGeneral:
          "Error al cargar los datos de la comunicación. Por favor, intenta nuevamente.",
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    if (field === "asunto") {
      const cleanValue = value.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ0-9\s]/g, "").toUpperCase();
      setFormData((prev) => ({ ...prev, [field]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEditorContenido = useCallback((value) => {
    setContenido(value);
  }, []);

  const handleDestinatarioChange = (index, field) => (event) => {
    const value = event.target.value;
    const updatedDestinatarios = [...formData.destinatarios];
    updatedDestinatarios[index] = { ...updatedDestinatarios[index], [field]: value };
    setFormData((prev) => ({ ...prev, destinatarios: updatedDestinatarios }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateEmailInput = (value) => {
    let cleanValue = value.trim();
    cleanValue = cleanValue.toLowerCase();
    cleanValue = cleanValue.replace(/[^a-z0-9@._-]/g, "");
    return {
      value: cleanValue,
      isValid: isValidEmail(cleanValue),
      errorMessage: isValidEmail(cleanValue) ? "" : "Por favor ingrese un Email válido",
    };
  };

  const handleEmailChange = (index, value) => {
    const result = validateEmailInput(value);
    const email = result.value;
    const updatedDestinatarios = [...formData.destinatarios];
    updatedDestinatarios[index] = { ...updatedDestinatarios[index], email: email };
    setFormData((prev) => ({ ...prev, destinatarios: updatedDestinatarios }));
  };

  const addDestinatario = () => {
    setFormData((prev) => ({
      ...prev,
      destinatarios: [...prev.destinatarios, { nombre_completo: "", notificado: false }],
    }));
  };

  const removeDestinatario = (index) => {
    const updatedDestinatarios = formData.destinatarios.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      destinatarios: updatedDestinatarios.map((destinatario) => ({ ...destinatario })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Limpiar errores previos
    setErrors({});
    clearError();

    // Validaciones básicas
    if (!formData.asunto || formData.asunto.length < 5) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral:
          "El Asunto es un campo obligatorio y debe superar una longitud de 5 caracteres.",
      }));
      return;
    }

    const parser = new DOMParser();
    const docContenido = parser.parseFromString(contenido, "text/html");
    const htmlLimpioContenido = docContenido.body.innerHTML;
    const longitudMinima = 50;

    if (!htmlLimpioContenido || htmlLimpioContenido.length < longitudMinima) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: `El Contenido es un campo obligatorio y debe superar una longitud de ${longitudMinima} caracteres.`,
      }));
      return;
    }

    if (formData.destinatarios.length < 1) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "Debe tener al menos un Destinatario.",
      }));
      return;
    }

    const confirmAction = window.confirm(`¿Estás seguro(a) que deseas actualizar la Comunicación?`);
    if (!confirmAction) return;

    try {
      setSaving(true);

      // 1. Limpiar imágenes huérfanas antes de actualizar
      console.log("Limpiando imágenes huérfanas...");
      try {
        const cleanResult = await cleanOrphanImages({ contenido: htmlLimpioContenido });
        if (cleanResult.success) {
          console.log(
            `Limpieza completada: ${cleanResult.imagenes_conservadas} imágenes conservadas`
          );
        } else {
          console.warn("Error en limpieza de imágenes:", cleanResult.error);
          // Continuar aunque falle la limpieza
        }
      } catch (cleanError) {
        console.warn("Error ejecutando limpieza de imágenes:", cleanError);
        // Continuar aunque falle la limpieza
      }

      // 2. Actualizar la comunicación
      const dataToUpdate = {
        proceso: comunicacion.proceso_id,
        audiencia: comunicacion.audiencia_id,
        asunto: formData.asunto,
        contenido: htmlLimpioContenido,
        destinatarios: formData.destinatarios.map((dest) => ({
          ...dest,
          // Preservar el estado de notificación si ya existe
          notificado: dest.notificado !== undefined ? dest.notificado : false,
          pdf_url: dest.pdf_url || null,
        })),
      };

      console.log("Actualizando comunicación...");
      await ComunicacionesApi.actualizarComunicacion(comunicacion.id, dataToUpdate);

      console.log("Comunicación actualizada exitosamente");
      onSuccess();
      hCClose();
    } catch (error) {
      console.error("Error al actualizar comunicación:", error);
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "Error al actualizar la comunicación. Por favor, intenta nuevamente.",
      }));
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setErrors({});
    setContenido("");
    setFormData({
      asunto: "",
      contenido: "",
      destinatarios: [],
    });
  };

  const hCClose = () => {
    if (!saving) {
      resetState();
      onClose();
    }
  };

  // Obtener módulos y formatos de Quill del hook
  const modules = getQuillModules();
  const formats = getQuillFormats();

  // Funciones para mostrar información del proceso y tipo (solo lectura)
  const getTipoComunicacionIcon = () => {
    if (comunicacion?.tipo_comunicacion === "EMAIL") {
      return <EmailIcon color="primary" fontSize="small" sx={{ mr: 1 }} />;
    } else if (comunicacion?.tipo_comunicacion === "FISICA") {
      return <LocalShippingIcon color="secondary" fontSize="small" sx={{ mr: 1 }} />;
    }
    return null;
  };

  const getFormatoComunicacion = () => {
    return comunicacion?.formato_comunicacion || "No especificado";
  };

  return (
    <>
      {(saving || loadingData) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
          <SuiBox ml={2}>
            <SuiTypography variant="h6" color="white">
              {loadingData && "Cargando datos..."}
              {saving && "Actualizando comunicación..."}
            </SuiTypography>
          </SuiBox>
        </Backdrop>
      )}

      <Dialog open={open} onClose={hCClose} maxWidth="lg" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h3" fontWeight="bold">
                  Editar Comunicación
                </SuiTypography>
              </Grid>
              {comunicacion && (
                <Grid size={6}>
                  <SuiTypography variant="h3" fontWeight="bold">
                    Creada:{" "}
                    {comunicacion.fecha_creacion
                      ? new Date(comunicacion.fecha_creacion).toLocaleString("es-ES")
                      : "No disponible"}
                  </SuiTypography>
                </Grid>
              )}
            </Grid>

            {errors.errorGeneral && (
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={12}>
                  <Alert severity="error" sx={{ marginBottom: "16px" }}>
                    {errors.errorGeneral}
                  </Alert>
                </Grid>
              </Grid>
            )}

            {imageError && (
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={12}>
                  <Alert severity="warning" sx={{ marginBottom: "16px" }}>
                    {imageError}
                  </Alert>
                </Grid>
              </Grid>
            )}

            {/* Campos de solo lectura: Proceso y Tipo */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="bold">
                  Proceso (No modificable)
                </SuiTypography>
                <SuiBox
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    backgroundColor: "#f5f5f5",
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SuiTypography variant="body2" color="text">
                    {comunicacion?.cliente_nombre
                      ? `${comunicacion.cliente_nombre} - ${comunicacion.proceso_identificador}`
                      : "Proceso no disponible"}
                  </SuiTypography>
                </SuiBox>
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="bold">
                  Tipo de Comunicación (No modificable)
                </SuiTypography>
                <SuiBox
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    backgroundColor: "#f5f5f5",
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {getTipoComunicacionIcon()}
                  <SuiTypography variant="body2" color="text">
                    {comunicacion?.tipo_comunicacion || "No especificado"} |{" "}
                    {getFormatoComunicacion()}
                  </SuiTypography>
                </SuiBox>
              </Grid>
            </Grid>

            {/* Campo editable: Asunto */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiTypography variant="h6" fontWeight="bold">
                  Asunto
                </SuiTypography>
                <SuiInput
                  value={formData.asunto || ""}
                  onChange={handleChange("asunto")}
                  fullWidth
                  disabled={loadingData}
                />
              </Grid>
            </Grid>

            {/* Campo editable: Contenido */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <SuiTypography variant="h6" fontWeight="bold">
                    Contenido
                  </SuiTypography>
                  {uploadingImage && (
                    <SuiBox display="flex" alignItems="center">
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <SuiTypography variant="caption" color="text">
                        Subiendo imagen...
                      </SuiTypography>
                    </SuiBox>
                  )}
                </SuiBox>
                <StyledEditor
                  ref={quillRef}
                  value={contenido}
                  onChange={handleEditorContenido}
                  modules={modules}
                  formats={formats}
                  readOnly={loadingData}
                />
              </Grid>
            </Grid>

            {/* Sección de Destinatarios */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox display="flex" alignItems="left" sx={{ mb: 1, mt: 2 }}>
                  <SuiTypography variant="h6" fontWeight="dark" sx={{ mr: 3 }}>
                    Destinatarios
                  </SuiTypography>
                  <SuiButton
                    variant="gradient"
                    buttonColor="dark"
                    size="small"
                    onClick={addDestinatario}
                    disabled={loadingData}
                  >
                    <AddIcon />
                  </SuiButton>
                </SuiBox>
              </Grid>
            </Grid>

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                {formData.destinatarios && formData.destinatarios.length > 0
                  ? formData.destinatarios.map((destinatario, index) => (
                      <SuiBox key={index} alignItems="center" mb={2}>
                        {/* Indicador de estado de notificación */}
                        {destinatario.notificado && (
                          <SuiBox mb={1}>
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              <SuiTypography variant="caption">
                                Este destinatario ya ha sido notificado
                              </SuiTypography>
                            </Alert>
                          </SuiBox>
                        )}

                        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                          <Grid size={4}>
                            <SuiBox display="flex" alignItems="left">
                              <IconButton
                                onClick={() => removeDestinatario(index)}
                                color="error"
                                disabled={loadingData}
                              >
                                <DeleteIcon />
                              </IconButton>
                              <SuiInput
                                value={destinatario.nombre_completo || ""}
                                onChange={handleDestinatarioChange(index, "nombre_completo")}
                                placeholder="Nombre Completo"
                                fullWidth
                                disabled={loadingData}
                              />
                            </SuiBox>
                          </Grid>
                          <Grid size={4}>
                            <Select
                              value={destinatario.tipo_identificacion || ""}
                              onChange={handleDestinatarioChange(index, "tipo_identificacion")}
                              fullWidth
                              disabled={loadingData}
                            >
                              <MenuItem value="">Seleccione Tipo de Identificación</MenuItem>
                              <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                              <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                              <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                              <MenuItem value="PAS">Pasaporte</MenuItem>
                              <MenuItem value="OTRO">Otro</MenuItem>
                            </Select>
                          </Grid>
                          <Grid size={4}>
                            <SuiInput
                              value={destinatario.identificacion || ""}
                              onChange={handleDestinatarioChange(index, "identificacion")}
                              placeholder="Identificación, max. 20 dígitos."
                              disabled={loadingData}
                            />
                          </Grid>
                        </Grid>
                        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                          <Grid size={3}>
                            <SuiInput
                              value={destinatario.tipo || ""}
                              onChange={handleDestinatarioChange(index, "tipo")}
                              placeholder="Tipo Destinatario"
                              fullWidth
                              disabled={loadingData}
                            />
                          </Grid>
                          <Grid size={3}>
                            <SuiInput
                              value={destinatario.celular || ""}
                              onChange={handleDestinatarioChange(index, "celular")}
                              placeholder="No. Celular"
                              fullWidth
                              disabled={loadingData}
                            />
                          </Grid>
                          <Grid size={3}>
                            <SuiInput
                              value={destinatario.email || ""}
                              onChange={(e) => handleEmailChange(index, e.target.value)}
                              placeholder="Correo Electrónico"
                              fullWidth
                              disabled={loadingData}
                            />
                          </Grid>
                          <Grid size={3}>
                            <SuiInput
                              value={destinatario.direccion || ""}
                              onChange={handleDestinatarioChange(index, "direccion")}
                              placeholder="Dirección"
                              fullWidth
                              disabled={loadingData}
                            />
                          </Grid>
                        </Grid>
                      </SuiBox>
                    ))
                  : !loadingData && (
                      <SuiBox textAlign="center" py={2}>
                        <SuiTypography variant="body2" color="text">
                          No hay destinatarios. Haz clic en el botón + para agregar uno.
                        </SuiTypography>
                      </SuiBox>
                    )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <SuiButton onClick={hCClose} buttonColor="light" disabled={saving}>
              Cancelar
            </SuiButton>
            <SuiButton
              type="submit"
              variant="gradient"
              buttonColor="dark"
              disabled={saving || loadingData || uploadingImage}
            >
              {saving ? "Actualizando..." : "Actualizar"}
            </SuiButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

EditComunicacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  comunicacion: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    proceso_id: PropTypes.number,
    audiencia_id: PropTypes.number,
    proceso_identificador: PropTypes.string,
    cliente_nombre: PropTypes.string,
    tipo_comunicacion: PropTypes.string,
    formato_comunicacion: PropTypes.string,
    asunto: PropTypes.string,
    contenido: PropTypes.string,
    destinatarios: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    fecha_creacion: PropTypes.string,
  }),
};

export default EditComunicacion;
